import Accordion from 'LuxKit/Accordion'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import React from 'react'

export default function TravellerManagement() {
  return (
    <Group direction="vertical" gap={8}>
      <Heading variant="heading4">Traveller Management</Heading>
      <Accordion size="large" heading="How do I invite employees once I have created my Business Traveller account?">
        <BodyText variant="large">
          Once you've created your account, inviting staff members is a simple process. Here's a step-by-step guide:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access Your Admin Dashboard:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and select the 'Traveller' tab in your admin dashboard.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Add Traveller:</BodyText>
              <BodyText variant="large"> In the 'People' tab, you'll find an option to add a traveller. Click on this option to proceed.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Enter Details:</BodyText>
              <BodyText variant="large"> You'll be prompted to enter the full name and email address of the staff member you wish to invite to Business Traveller. Please make sure to enter accurate information.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Select Role:</BodyText>
              <BodyText variant="large"> When adding a staff member, you'll also have the opportunity to assign a role. You can choose 'Admin' or 'Traveller' based on the access and permissions you wish to grant.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Send Invitation:</BodyText>
              <BodyText variant="large"> Once you've entered the necessary details and selected the appropriate role, simply select the 'Invite' button. An invitation email will be sent to the staff member, providing them with instructions on how to join your Business Traveller account.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How do I bulk invite staff to my account?">
        <BodyText variant="large">
          If you're an admin looking to bulk add staff members to your Business Traveller account, you can do so through your account manager. Here's how:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Contact Your Account Manager:</BodyText>
              <BodyText variant="large"> Reach out to your designated account manager with the list of staff members you wish to add in bulk. Provide the full names and email addresses of each staff member.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Specify Roles:</BodyText>
              <BodyText variant="large"> You will also need to specify the roles you want to assign to each staff member. You can assign either 'Admin' or 'Traveller' based on your organisational structure and their role within your business.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Submit the Request:</BodyText>
              <BodyText variant="large"> Your account manager will process your request. They will ensure that all staff members are added to your account with the specified roles.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Confirmation:</BodyText>
              <BodyText variant="large"> Once the bulk addition process is complete, you'll receive confirmation from your account manager. You can then inform your staff members that they've been added to the Business Traveller account.</BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          By utilising this process through your account manager, you can efficiently add multiple staff members to your Business Traveller account and streamline your corporate travel management efforts.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="What are the roles available on Business Traveller?">
        <BodyText variant="large">
          You can assign roles to team members to define their level of access and permissions within Business Traveller.
        </BodyText>
        <br />
        <BodyText variant="large">
          There are two roles that can be assigned:
        </BodyText>
        <br />
        <BodyText variant="large" weight="bold">
          Traveller
        </BodyText>
        <BodyText variant="large">
          Travellers can only book and view their own travel on the Business Traveller Homepage. They will not have dashboard access.
        </BodyText>
        <br />
        <BodyText variant="large" weight="bold">
          Admin
        </BodyText>
        <BodyText variant="large">
          Admins have dashboard access and the ability to manage accounts and create travel policies, create and modify bookings. Admins also have expense management permissions.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="How can I change roles for an existing user on my Business Traveller account?">
        <BodyText variant="large">
          If you need to change the role of an existing user within your Business Traveller account, you can easily do so through the 'People' tab. Here's how:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access the 'People' Tab:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and navigate to the 'People' tab in your admin dashboard. Here, you'll find a comprehensive table listing all your travellers.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Locate the User:</BodyText>
              <BodyText variant="large"> Find the user whose role you wish to change.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Change the Role:</BodyText>
              <BodyText variant="large"> Hover over the role designation of the user you want to modify. A dropdown menu will appear, allowing you to select the preferred role for that user.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Select the Preferred Role:</BodyText>
              <BodyText variant="large"> From the dropdown menu, choose the new role you want to assign to the user. You can select between 'Admin' or 'Traveller' based on their responsibilities and access requirements.</BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large">
          Once you've selected the new role, the user's role will be updated accordingly, granting them appropriate access and permissions within your Business Traveller account.
        </BodyText>
      </Accordion>
      <Accordion size="large" heading="Who should be an admin vs employee on Business Traveller? What access does an admin have vs an employee?">
        <BodyText variant="large" weight="bold">Admin:</BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large">
                Admins may include managers, team leaders, or individuals responsible for overseeing corporate travel management.
              </BodyText>
            </li>
            <li>
              <BodyText variant="large">
                Admins have comprehensive access to the Business Traveller account, allowing them to manage bookings, settings and user permissions.
              </BodyText>
            </li>
            <li>
              <BodyText variant="large">
                Admins can add or remove staff members, modify user roles, customise travel policies and generate reports.
              </BodyText>
            </li>
          </Group>
        </ol>
        <BodyText variant="large" weight="bold">Employee (traveller):</BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large">
                Employees, or 'Travellers,' are the end-users who will utilise the Business Traveller platform to book and manage their travel arrangements.
              </BodyText>
            </li>
            <li>
              <BodyText variant="large">
                Travellers have access to book flights, hotels and transportation, as well as view upcoming trip details.
              </BodyText>
            </li>
            <li>
              <BodyText variant="large">
                Traveller access may be limited to specific functionalities based on the permissions set by admins, ensuring compliance with company policies and travel guidelines.
              </BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="If a staff member leaves the business, how do I remove their access to Business Traveller?">
        <BodyText variant="large">
          When a staff member departs the business, it's essential for an Admin to promptly revoke their access to Business Traveller in order to maintain security and privacy. Here's how you can remove a staff member's access:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access the Admin Dashboard:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and navigate to the admin dashboard.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Go to the 'People' Tab:</BodyText>
              <BodyText variant="large"> Locate the 'People' tab within the dashboard. Here, you'll find a list of all users with access to the Business Traveller account.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Find the Departing Staff Member:</BodyText>
              <BodyText variant="large"> Identify the staff member who has left the business from the list of users.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Deactivate:</BodyText>
              <BodyText variant="large"> Next to the departing staff member's name, you'll see an option in the actions icon to manage their access or role. Select the 'Deactivate' option to proceed. Admins will still be able to review a record of the trips made by a Traveller once removed.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
    </Group>
  )
}
